import type {
    SketchRegionFaceLoop,
    SketchRegionGraphEdge,
    SketchRegionVec2,
    SketchRegionVec3,
} from './SketchRegionTypes';
import { signedArea2 } from './SketchRegionMath';

/**
 * 邻接边。
 */
interface SketchRegionAdjacency {
    /** 邻点键。 */
    key: string;
    /** 指向邻点的角度。 */
    angle: number;
}

/**
 * 图顶点记录。
 */
interface SketchRegionGraphVertex {
    /** 顶点键。 */
    key: string;
    /** 二维点。 */
    point: SketchRegionVec2;
    /** 三维点。 */
    point3: SketchRegionVec3;
    /** 邻接点。 */
    neighbors: SketchRegionAdjacency[];
}

/**
 * 生成有向边键。
 * @param start 起点键。
 * @param end 终点键。
 * @returns 有向边键。
 */
function directedKey(start: string, end: string): string {
    return `${start}->${end}`;
}

/**
 * 生成无向边键。
 * @param start 起点键。
 * @param end 终点键。
 * @returns 无向边键。
 */
function undirectedKey(start: string, end: string): string {
    return start < end ? `${start}|${end}` : `${end}|${start}`;
}

/**
 * 构建顶点表。
 * @param edges 图边。
 * @returns 顶点表。
 */
function buildVertices(edges: SketchRegionGraphEdge[]): Map<string, SketchRegionGraphVertex> {
    const vertices = new Map<string, SketchRegionGraphVertex>();
    const ensureVertex = (
        key: string,
        point: SketchRegionVec2,
        point3: SketchRegionVec3,
    ): SketchRegionGraphVertex => {
        const existing = vertices.get(key);
        if (existing) {
            return existing;
        }
        const vertex: SketchRegionGraphVertex = {
            key,
            point,
            point3,
            neighbors: [],
        };
        vertices.set(key, vertex);
        return vertex;
    };

    edges.forEach((edge) => {
        const start = ensureVertex(edge.startKey, edge.start, edge.start3);
        const end = ensureVertex(edge.endKey, edge.end, edge.end3);
        start.neighbors.push({
            key: end.key,
            angle: Math.atan2(end.point[1] - start.point[1], end.point[0] - start.point[0]),
        });
        end.neighbors.push({
            key: start.key,
            angle: Math.atan2(start.point[1] - end.point[1], start.point[0] - end.point[0]),
        });
    });

    vertices.forEach((vertex) => {
        vertex.neighbors.sort((left, right) => left.angle - right.angle);
    });
    return vertices;
}

/**
 * 查找下一条保持左侧面遍历的有向边。
 * @param vertices 顶点表。
 * @param previous 当前边起点。
 * @param current 当前边终点。
 * @returns 下一邻点键。
 */
function resolveNextVertex(
    vertices: Map<string, SketchRegionGraphVertex>,
    previous: string,
    current: string,
): string | null {
    const currentVertex = vertices.get(current);
    if (!currentVertex || currentVertex.neighbors.length === 0) {
        return null;
    }
    /*
     * 普通边界角点（如直线-圆弧接头）在平面图里 degree=2。
     * 此时只需沿边界继续前进到“不是来向 previous 的唯一邻点”，
     * 不能盲用左侧面规则，否则会在槽口角点沿弧折返并重复访问同一顶点。
     */
    if (currentVertex.neighbors.length === 2) {
        const outgoing = currentVertex.neighbors.find((neighbor) => neighbor.key !== previous);
        return outgoing?.key ?? null;
    }
    const incomingIndex = currentVertex.neighbors.findIndex((neighbor) => neighbor.key === previous);
    if (incomingIndex < 0) {
        return null;
    }
    const nextIndex = (incomingIndex - 1 + currentVertex.neighbors.length) % currentVertex.neighbors.length;
    return currentVertex.neighbors[nextIndex]!.key;
}

/**
 * 将边界实体去重排序。
 * @param values 原始值。
 * @returns 去重后的值。
 */
function uniqueSorted(values: string[]): string[] {
    return Array.from(new Set(values.filter((value) => value.length > 0))).sort();
}

/**
 * 追踪草图平面图中的闭合区域。
 * @param edges 图边。
 * @returns 闭合面环。
 */
export function traceSketchRegionFaces(edges: SketchRegionGraphEdge[]): SketchRegionFaceLoop[] {
    const vertices = buildVertices(edges);
    const edgeMap = new Map(edges.map((edge) => [undirectedKey(edge.startKey, edge.endKey), edge]));
    const visited = new Set<string>();
    const loops: SketchRegionFaceLoop[] = [];

    edges.forEach((edge) => {
        [
            [edge.startKey, edge.endKey],
            [edge.endKey, edge.startKey],
        ].forEach(([initialStart, initialEnd]) => {
            const firstDirectedKey = directedKey(initialStart!, initialEnd!);
            if (visited.has(firstDirectedKey)) {
                return;
            }

            let startKey = initialStart!;
            let endKey = initialEnd!;
            const vertexKeys: string[] = [];
            const points: SketchRegionVec2[] = [];
            const points3: SketchRegionVec3[] = [];
            const boundaryEntityIds: string[] = [];
            const closedBoundaryEntityIds: string[] = [];
            const splitterEntityIds: string[] = [];
            const loopEdges: SketchRegionFaceLoop['edges'] = [];

            for (let guard = 0; guard < edges.length * 4; guard += 1) {
                const currentDirectedKey = directedKey(startKey, endKey);
                if (visited.has(currentDirectedKey)) {
                    break;
                }
                visited.add(currentDirectedKey);

                const startVertex = vertices.get(startKey);
                const endVertex = vertices.get(endKey);
                const sourceEdge = edgeMap.get(undirectedKey(startKey, endKey));
                if (!startVertex || !endVertex || !sourceEdge) {
                    break;
                }
                vertexKeys.push(startKey);
                points.push(startVertex.point);
                points3.push(startVertex.point3);
                boundaryEntityIds.push(...sourceEdge.entityIds);
                closedBoundaryEntityIds.push(...sourceEdge.closedEntityIds);
                splitterEntityIds.push(...sourceEdge.openEntityIds);
                loopEdges.push({
                    start3: startVertex.point3,
                    end3: endVertex.point3,
                    entityIds: [...sourceEdge.entityIds],
                    forward: sourceEdge.startKey === startKey && sourceEdge.endKey === endKey,
                });

                const nextKey = resolveNextVertex(vertices, startKey, endKey);
                if (!nextKey) {
                    break;
                }
                startKey = endKey;
                endKey = nextKey;
                if (startKey === initialStart && endKey === initialEnd) {
                    const area = signedArea2(points);
                    if (area > 1e-8 && points.length >= 3) {
                        loops.push({
                            vertexKeys,
                            points,
                            points3,
                            boundaryEntityIds: uniqueSorted(boundaryEntityIds),
                            closedBoundaryEntityIds: uniqueSorted(closedBoundaryEntityIds),
                            splitterEntityIds: uniqueSorted(splitterEntityIds),
                            edges: loopEdges,
                            area,
                        });
                    }
                    break;
                }
            }
        });
    });

    return loops;
}
